import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { PLAN_CODE } from "@/server/types/billing";

/** Warning threshold. Flash orange once usage crosses 80% of the Free-tier cap. */
const WARN_PCT = 0.8;

/**
 * Dashboard-wide banner that surfaces when the caller's org is approaching or
 * at the Free-tier patient cap. Renders nothing on non-free plans and for orgs
 * whose billing_state row is missing. CTA links to the Billing section where
 * they can contact support to upgrade.
 */
export function QuotaWarningBanner() {
  const { data: state } = useBillingStateQuery();
  if (!state) return null;
  if (state.plan_code !== PLAN_CODE.FREE) return null;
  if (state.active_patient_limit <= 0) return null;

  const ratio = state.active_patient_count / state.active_patient_limit;
  if (ratio < WARN_PCT) return null;

  const overCap = state.active_patient_count >= state.active_patient_limit;
  const tone = overCap
    ? "bg-destructive/10 border-destructive/30 text-destructive"
    : "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400";
  const message = overCap
    ? "You've hit the Free plan patient limit. Contact support to upgrade."
    : `You're at ${Math.round(ratio * 100)}% of your Free plan patient limit.`;

  return (
    <div className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-sm ${tone}`}>
      <AlertTriangle className="size-4 shrink-0" />
      <span className="flex-1">{message}</span>
      <Link
        to="/dashboard/settings"
        search={{ section: "usage", destinationId: undefined, provider: "", webhookId: undefined }}
        className="font-medium underline whitespace-nowrap"
      >
        View usage
      </Link>
    </div>
  );
}
